import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { BrandMark } from "../components/Brand";

const STROKE = 1.5;
const ROLE_LABEL = {
  admin: "Admin",
  recruiter: "Recruiter",
  hiring_manager: "Hiring Manager",
  viewer: "Viewer",
};

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");
  const { user, loginWithToken } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    api
      .get(`/team/invite-info/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((err) => setError(err?.response?.data?.detail || "Invite not found"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async (e) => {
    e?.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = { token };
      if (!user) {
        payload.password = password;
        payload.name = name;
      }
      const { data } = await api.post("/team/accept-invite", payload);
      await loginWithToken(data.access_token);
      navigate(next || "/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to accept invite");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50" data-testid="accept-invite-page">
      <header className="border-b border-ink-200 bg-white">
        <div className="hp-container flex h-16 items-center justify-between">
          <Link to="/"><BrandMark /></Link>
        </div>
      </header>
      <main className="flex items-start justify-center px-6 py-16">
        <div className="w-full max-w-[460px]">
          {loading && (
            <div className="flex items-center gap-2 text-ink-500">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> Loading invite…
            </div>
          )}
          {!loading && !info && (
            <div
              className="hp-card p-6 text-center"
              data-testid="invite-not-found"
            >
              <h1 className="font-display text-2xl font-medium text-ink-900">
                Invite not found
              </h1>
              <p className="mt-2 text-[14px] text-ink-500">
                This invite link is invalid or has been revoked.
              </p>
            </div>
          )}
          {!loading && info && info.status !== "pending" && (
            <div className="hp-card p-6 text-center" data-testid="invite-not-pending">
              <h1 className="font-display text-2xl font-medium text-ink-900">
                Invite {info.status}
              </h1>
              <p className="mt-2 text-[14px] text-ink-500">
                This invite is no longer active.
              </p>
              <Link to="/signin" className="hp-btn-outline mt-6" data-testid="invite-signin">
                Sign in
              </Link>
            </div>
          )}
          {!loading && info && info.status === "pending" && (
            <>
              <div className="hp-section-label">You&rsquo;ve been invited</div>
              <h1 className="font-display mt-2 text-3xl font-medium tracking-tight text-ink-900">
                Join {info.workspace_name}
              </h1>
              <p className="mt-2 text-[14px] text-ink-500">
                As <span className="font-medium text-ink-900">{ROLE_LABEL[info.role]}</span>,
                for{" "}
                <span className="font-medium text-ink-900">{info.email}</span>.
              </p>
              {next && (
                <p className="mt-2 text-[13px] text-ink-500" data-testid="invite-next-hint">
                  You&rsquo;ll be taken straight to your assigned interview after joining.
                </p>
              )}

              <form onSubmit={handleAccept} className="mt-8 space-y-4" data-testid="accept-form">
                {!user && (
                  <>
                    <div>
                      <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                        Full name
                      </label>
                      <input
                        required
                        type="text"
                        className="hp-input mt-1.5"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="accept-name"
                      />
                    </div>
                    <div>
                      <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                        Choose a password (min 8 chars)
                      </label>
                      <input
                        required
                        minLength={8}
                        type="password"
                        className="hp-input mt-1.5"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="accept-password"
                      />
                    </div>
                  </>
                )}
                {error && (
                  <div
                    className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700"
                    data-testid="accept-error"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={STROKE} />
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="hp-btn-primary w-full"
                  data-testid="accept-submit"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
                  Accept invite & continue
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
